import {useLazyQuery} from "@apollo/client";
import {RefObject, useState} from "react";
import toast from "react-hot-toast";

import {DropdownItem} from "@/components/dropdown/subComponents/DropdownItem/DropdownItem.tsx";
import {DropdownButton} from "@/components/dropdownButton/DropdownButton.tsx";
import {GQL_METRICS_EXPLORER_EXPORT, MetricsExplorerExportData} from "@/lib/queries/metricsExplorer.ts";
import {MetricsExplorerExportFormat, MetricsExplorerQueryInput} from "@/lib/types/metricsExplorer.ts";
import {downloadBase64File} from "@/lib/utils/downloadFile.ts";
import {captureChartSvg} from "@/pages/metricsExplorer/exportChart.ts";

interface ExportChartButtonProps {
  query: MetricsExplorerQueryInput | null;
  checkNames: Record<number, string>;
  chartRef: RefObject<HTMLDivElement | null>;
}

export const ExportChartButton = ({query, checkNames, chartRef}: ExportChartButtonProps) => {
  const [runExport] = useLazyQuery<MetricsExplorerExportData>(GQL_METRICS_EXPLORER_EXPORT, {
    fetchPolicy: "no-cache",
  });
  const [busy, setBusy] = useState(false);

  const handleExport = async (format: MetricsExplorerExportFormat) => {
    if (!query || busy) {
      return;
    }
    // The PDF is the chart as drawn, so there has to be a chart drawn.
    const chartSvg = format === "PDF" ? captureChartSvg(chartRef.current) : null;
    if (format === "PDF" && !chartSvg) {
      toast.error("Draw a chart before exporting it to PDF.");
      return;
    }
    setBusy(true);
    try {
      const {data, error} = await runExport({
        variables: {
          input: {
            query,
            format,
            chart_svg: chartSvg,
            check_names: Object.entries(checkNames).map(([checkId, name]) => ({
              check_id: Number(checkId),
              name,
            })),
          },
        },
      });
      const file = data?.metricsExplorerExport;
      if (error || !file) {
        toast.error(error?.message ?? "Could not build the export.");
        return;
      }
      downloadBase64File(file.data, file.filename, file.content_type);
      if (file.truncated) {
        toast("The spreadsheet hit the row limit and does not contain every datapoint.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownButton title={busy ? "Preparing…" : "Export"} size="small" disabled={!query || busy}>
      <DropdownItem onClick={() => handleExport("PDF")}>Chart and analysis (PDF)</DropdownItem>
      <DropdownItem onClick={() => handleExport("XLS")}>Data and analysis (Excel)</DropdownItem>
    </DropdownButton>
  );
};
